import { Button, Empty, Spin, Modal, message, Popconfirm } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState, useRef } from 'react';
import { PlusOutlined, PlayCircleOutlined, PictureOutlined, PauseCircleOutlined, EditOutlined, ScissorOutlined, GatewayOutlined, HolderOutlined, UserOutlined, DeleteOutlined } from '@ant-design/icons';
import { getVideoPreview } from 'polotno/utils/video';
import { useGlobalContext } from '@src/context/globalContext';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './sceneMedia.less';
import { VideoPanel } from './customPolotno/customVideoSearchPanelTwo';
import SceneMediaTrimmer from './sceneMediaTrimmer';
import CropMedia from './cropMedia';
import SelectMediaModal from './selectMediaModal';
import { SectionPreviewButton } from './sectionPreviewContainer';
import { prefetchAsset } from '@src/shared/utils/remotion';
import ReplicaSelectionModal from "../../../shared/components/replicaSelectionModal";
import { aiTextToSpeech, generateCaptions, getPrevNextText, getAudioDurationInSecondsCustom } from '@src/shared/utils/VideoGeneration/audioGeneration';
import { getToken, getDomainID } from '@src/config/config';
import { useGetVoices } from '@src/api/elevenLabs/getVoices';
import { transitionAnimationsDropDown } from '@src/data/transitions';

interface SceneMediaProps {
  store: any;
  onBack: () => void;
}

const SceneMedia = observer(({ store }: SceneMediaProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [jsonPages, setJsonPages] = useState<any>(null);
  const [sectionPages, setSectionPages] = useState<any[]>([]);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [isTrimmerModalOpen, setIsTrimmerModalOpen] = useState(false);
  const [trimmingElementId, setTrimmingElementId] = useState<string | null>(null);
  const [trimmingVideoUrl, setTrimmingVideoUrl] = useState<string | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [croppingMedia, setCroppingMedia] = useState<{ url: string; isVideo: boolean; elementId: string; pageId: string } | null>(null);
  const [isSelectMediaModalOpen, setIsSelectMediaModalOpen] = useState(false);
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const sectionsContainerRef = useRef<HTMLDivElement>(null);
  const [modalFor, setModalFor] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false)
  const { data: aiVoiceData } = useGetVoices();
  const {
    ScriptDetails: [scriptDetails, setScriptDetails],
    SelectedSectionIndex: [selectedSectionIndex, setSelectedSectionIndex],
    ActiveElementId: [activeElementId,setActiveElementId],
    UserDetail: [userDetail, setUserDetail],
    UndoStack: [undoStack, setUndoStack],
    CurrentUserData: [currentUserData]
  } = useGlobalContext();
  const [replicaModalOpen, setReplicaModalOpen] = useState(false);
  const [currentMediaId, setCurrentMediaId] = useState('');
  const [avatarSelectionLoading, setAvatarSelectionLoading] = useState(false);
  const [showAvatarChangeModal, setShowAvatarChangeModal] = useState(false);
  const [pendingAvatarChange, setPendingAvatarChange] = useState<{replicaId: string, previewUrl: string, voiceId?: string, updateAllScenes?: boolean, avatarProvider?: 'tavus' | 'heygen'} | null>(null);
  const [showPageScopeModal, setShowPageScopeModal] = useState(false);
  const [pendingPageScopeChange, setPendingPageScopeChange] = useState<{replicaId: string, previewUrl: string, voiceId?: string, avatarProvider?: 'tavus' | 'heygen'} | null>(null);
  const groupPagesByScriptIndex = (pages: any[]) => {
    return pages.reduce((groups: any, page: any) => {
      const scriptIndex = page.custom?.scriptIndex || 0;
      if (!groups[scriptIndex]) {
        groups[scriptIndex] = [];
      }
      groups[scriptIndex].push(page);
      return groups;
    }, {});
  };

  useEffect(() => {
    if (jsonPages?.pages) {
      const groupedPages = groupPagesByScriptIndex(jsonPages.pages);
      const sectionPages = groupedPages[selectedSectionIndex] || [];
      setSectionPages(sectionPages);
      setIsLoading(false);
    }
  }, [jsonPages, selectedSectionIndex]);

  useEffect(() => {
    setJsonPages(store.toJSON());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, selectedSectionIndex, store.activePage, store.pages, store.pages.map((page: { duration: number }) => page.duration).join(',')]);

  // Add effect to scroll to selected section
  useEffect(() => {
    if (selectedSectionIndex !== null && sectionsContainerRef.current) {
      const sectionElements = sectionsContainerRef.current.getElementsByClassName('section-container');
      if (sectionElements[selectedSectionIndex]) {
        sectionElements[selectedSectionIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedSectionIndex]);
  const [selectedSubIndex, setSelectedSubIndex] = useState(0);
  const handleCardClick = (pageId: string, event: React.MouseEvent, sectionIndex: number, mediaId: string, index: number) => {
    setSelectedSubIndex(index);
    const deepCopyScriptDetails = JSON.parse(JSON.stringify(scriptDetails))
    setUndoStack([...undoStack, deepCopyScriptDetails])
    const findPageMedia = sectionPages.find((page: any) => page.id === pageId)?.children.filter((item: any) => item.type === 'video');

    if (findPageMedia?.length > 0) {
      // If it's a video, handle play/pause
      if (playingVideo === pageId) {
        handleVideoPause(pageId, event);
      } else {
        handleVideoPlay(pageId, event);
      }
    } else {
      // If it's not a video, select the page
      handleSelectedPage(pageId);
    }
    store.selectPage(pageId);
    setSelectedSectionIndex(sectionIndex);
    setActiveElementId(mediaId)
  };

  useEffect(() => {
    // handleCardClick(store.activePage.id, event as any, selectedSectionIndex)
    setJsonPages(store.toJSON())
  }, [activeElementId])
  const handleVideoPlay = (pageId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setPlayingVideo(pageId);
    
    // Find and play the video element
    const videoElement = document.querySelector(`[data-page-id="${pageId}"] video`);
    if (videoElement) {
      (videoElement as HTMLVideoElement).play();
    }
  };

  const handleVideoPause = (pageId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setPlayingVideo(null);
    
    // Find and pause the video element
    const videoElement = document.querySelector(`[data-page-id="${pageId}"] video`);
    if (videoElement) {
      (videoElement as HTMLVideoElement).pause();
    }
  };

  const handleSelectedPage = (pageId: string) => {
    store.selectPage(pageId);
  };

  const handleAddMedia = (sectionIndex: number) => {
    if (!currentUserData?.isAllowedToEdit) {
      message.warning('Please upgrade to add media.')
      return
    }
    const deepCopyScriptDetails = JSON.parse(JSON.stringify(scriptDetails))
    setUndoStack([...undoStack, deepCopyScriptDetails])
    setIsModalOpen(true);
    const sectionPagesDynamic = jsonPages?.pages?.filter((page: any) => page?.custom?.scriptIndex === sectionIndex) || [];
    const lastPage = sectionPagesDynamic[sectionPagesDynamic.length - 1];
    const totalDurationBefore = sectionPagesDynamic.reduce((sum: number, page: any) => sum + page.duration, 0);
    store.getElementById(lastPage.id).clone();
    const updatedSectionPages = store.pages.filter((page: any) => page.custom?.scriptIndex === sectionIndex);
    const newDuration = totalDurationBefore / updatedSectionPages.length;
    updatedSectionPages.forEach((page: any) => {
      page.set({ duration: newDuration });
    });
    setJsonPages(store.toJSON());
    setScriptDetails((prevScript: { data: any }) => {
      const updatedScript = { ...prevScript }
      const updatedData = [...prevScript.data]
      const pages = store.toJSON().pages
      const totalPages = pages
        .filter((page: any) => page?.custom?.scriptIndex === sectionIndex)
        .map((page: any) => page.id)
      
      // Add one empty object to the images array for the new cloned page
      if (updatedData[sectionIndex]?.images) {
        updatedData[sectionIndex] = {
          ...updatedData[sectionIndex],
          images: [...updatedData[sectionIndex].images, { searchQueries: [''], duration: 0.9 }]
        }
      } else {
        const images = [];
        for (let i = 0; i < totalPages.length; i++) {
          images.push({ searchQueries: [''], duration: 0.9 })
        }
        updatedData[sectionIndex] = {
          ...updatedData[sectionIndex],
          images: images,
        }
      }
      
      return { ...updatedScript, data: updatedData }
    })
  };

  const handleEditClick = (pageId: string, mediaId: string, sectionIndex: number, event: React.MouseEvent, index: number) => {
    event.stopPropagation(); // Prevent card click when clicking edit icon
    if (!currentUserData?.isAllowedToEdit) {
      message.warning('Please upgrade to edit media.')
      return
    }
    setSelectedSubIndex(index);

    setEditingPageId(pageId);
    setIsModalOpen(true);
    store.selectPage(pageId);
    setActiveElementId(mediaId)
    setSelectedSectionIndex(sectionIndex);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingPageId(null);
  };

  const handleModalOk = () => {
    setIsModalOpen(false);
    setEditingPageId(null);
    setJsonPages(store.toJSON());
  };

  const getSceneStartTime = (pageId: string) => {
    let startTime = 0;
    // Find all pages before the current page in store.pages
    const currentPageIndex = store.pages.findIndex((page: any) => page.id === pageId);
    for (let i = 0; i < currentPageIndex; i++) {
      startTime += store.pages[i]?.duration || 0;
    }
    return (startTime / 1000).toFixed(1);
  };

  const getSceneEndTime = (pageId: string) => {
    let endTime = 0;
    // Find all pages up to and including the current page in store.pages
    const currentPageIndex = store.pages.findIndex((page: any) => page.id === pageId);
    for (let i = 0; i <= currentPageIndex; i++) {
      endTime += store.pages[i]?.duration || 0;
    }
    return (endTime / 1000).toFixed(1);
  };

  // Calculate duration from pages
  const calculateSectionDuration = (sectionIndex: number) => {
    const pages = store.toJSON().pages;
    const sectionPages = pages.filter((page: any) => page?.custom?.scriptIndex === sectionIndex);

    if (sectionPages.length === 0) return { startTime: 0, endTime: 0 };

    // Calculate start time by summing durations of previous sections
    let startTime = 0;
    for (let i = 0; i < sectionIndex; i++) {
      const prevSectionPages = pages.filter((page: any) => page?.custom?.scriptIndex === i);
      startTime += prevSectionPages.reduce((sum: number, page: any) => sum + (page.duration || 0), 0);
    }

    // Calculate end time by adding current section duration
    const currentSectionDuration = sectionPages.reduce((sum: number, page: any) => sum + (page.duration || 0), 0);
    const endTime = startTime + currentSectionDuration;

    return { startTime, endTime };
  };

  const handleDragEnd = (result: any, sectionIndex: number) => {
    if (!result.destination) return;

    // These are local indexes within the current section
    const localSourceIndex = result.source.index;
    const localDestinationIndex = result.destination.index;
    
    if (localSourceIndex === localDestinationIndex) return;
    
    // Get all pages from the store
    const allPages = store.pages;
    
    // Get all pages in this section
    const sectionPagesDynamic = jsonPages?.pages?.filter((page: any) => page?.custom?.scriptIndex === sectionIndex) || [];
    
    // Get the actual page objects being moved
    const sourcePageObj = sectionPagesDynamic[localSourceIndex];
    const destPageObj = sectionPagesDynamic[localDestinationIndex];
    
    // Find the global indexes in the entire jsonPages.pages array
    const globalSourceIndex = jsonPages?.pages?.findIndex((page: any) => page.id === sourcePageObj.id);
    const globalDestinationIndex = jsonPages?.pages?.findIndex((page: any) => page.id === destPageObj.id);
  
    // Create a copy of all pages to reorder
    const allPagesArray = [...(jsonPages?.pages || [])];
    
    // Remove the source page from its original position in the global array
    const [movedPage] = allPagesArray.splice(globalSourceIndex, 1);
    
    // Insert it at the new global position
    allPagesArray.splice(globalDestinationIndex, 0, movedPage);
    
    // Update z-index for all pages based on their new global positions
    allPagesArray.forEach((page, globalIndex) => {
      if (page && page.id) {
        const element = store.getElementById(page.id);
        if (element) {
          element.setZIndex(globalIndex);
        }
      }
    });
    
    // Now handle the local section reordering
    const reorderedSectionPages = [...sectionPagesDynamic];
    reorderedSectionPages.splice(localSourceIndex, 1);
    reorderedSectionPages.splice(localDestinationIndex, 0, sourcePageObj);
    
    // Update imageIndex for all pages in this section
    reorderedSectionPages.forEach((page, localIndex) => {
      const pageInStore = allPages.find((p: any) => p.id === page.id);
      
      if (pageInStore && page.id) {
        const element = store.getElementById(page.id);
        if (element) {
          // Update only the imageIndex while keeping the scriptIndex and other custom properties
          element.set({
            custom: {
              ...pageInStore.custom,
              scriptIndex: sectionIndex,
              imageIndex: localIndex // Local index within section
            }
          });
        }
      }
    });
    
    // Update script details to reflect the new page order
    setScriptDetails((prevScript: { data: any }) => {
      const updatedScript = { ...prevScript };
      const updatedData = [...prevScript.data];
      
      // Only update if we have data for this section
      if (updatedData[sectionIndex]?.images) {
        // Reorder the images array to match the new page order
        const updatedImages = [...updatedData[sectionIndex].images];
        const [movedImage] = updatedImages.splice(localSourceIndex, 1);
        updatedImages.splice(localDestinationIndex, 0, movedImage);
        
        updatedData[sectionIndex] = {
          ...updatedData[sectionIndex],
          images: updatedImages
        };
      }
      
      return { ...updatedScript, data: updatedData };
    });

    // Update store to reflect changes
    setJsonPages(store.toJSON());
  };

  const handleTrimClick = (pageId: string, mediaId: string, videoUrl: string, sectionIndex: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setModalFor('trim');
    setIsSelectMediaModalOpen(true);
    store.selectPage(pageId);
    setCurrentPageId(pageId);
    setSelectedSectionIndex(sectionIndex);
  };

  const handleTrimmerSave = () => {
    setIsTrimmerModalOpen(false);
    setTrimmingElementId(null);
    setTrimmingVideoUrl(null);
    setJsonPages(store.toJSON());
  };

  const handleTrimmerCancel = () => {
    setIsTrimmerModalOpen(false);
    setTrimmingElementId(null);
    setTrimmingVideoUrl(null);

  };

  const handleCropClick = (pageId: string, mediaId: string, mediaUrl: string, mediaType: string, sectionIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPageId(pageId);
    setModalFor('crop');
    setIsSelectMediaModalOpen(true);
    store.selectPage(pageId);
    setSelectedSectionIndex(sectionIndex);

  };

  const handleMediaSelect = (mediaId: string) => {
    const page = store.getElementById(currentPageId);

    if (page) {
      const selectedMedia = store.getElementById(mediaId);
      if (modalFor === 'crop') {
        if (selectedMedia) {
          setCroppingMedia({
            url: selectedMedia.src,
            isVideo: selectedMedia.type === 'video',
            elementId: selectedMedia.id,
            pageId: selectedMedia.id
          });
          setIsCropModalOpen(true);
        }
      } else if (modalFor === 'trim') {
        const selectedMedia = store.getElementById(mediaId);
        setTrimmingElementId(selectedMedia.id);
        setTrimmingVideoUrl(selectedMedia.src);
        setIsTrimmerModalOpen(true);
        setEditingPageId(currentPageId);
      }
      setIsSelectMediaModalOpen(false);

    };
  }

  const handleCropSave = (cropData: { x: number; y: number; width: number; height: number }) => {
    setIsCropModalOpen(false);
    setCroppingMedia(null);
  };

  const handleAvatarClick = (pageId: string, mediaId: string, sectionIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPageId(pageId);
    setCurrentMediaId(mediaId);
    setReplicaModalOpen(true);
    setSelectedSectionIndex(sectionIndex);
  };

  const onAvatarChangeClick = (replicaId: string, previewUrl: string, voiceId?: string, avatarProvider?: 'tavus' | 'heygen') => {
    // For HeyGen avatars, skip voice update modal and page scope modal - directly update avatar
    if (avatarProvider === 'heygen') {
      handleReplicaSelect(
        replicaId,
        previewUrl,
        undefined, // No voiceId for HeyGen
        false, // Don't update voice
        false, // Update only current scene by default
        'heygen'
      );
      return;
    }
    
    // For Tavus avatars, show the page scope modal first
    setPendingPageScopeChange({ replicaId, previewUrl, voiceId, avatarProvider });
    setShowPageScopeModal(true);
  };

  const handleReplicaSelect = async (
    replicaId: string,
    previewUrl: string,
    voiceId?: string,
    shouldUpdateVoice = true,
    updateAllScenes = false,
    avatarProvider: 'tavus' | 'heygen' = 'tavus'
  ) => {
    if (!currentPageId) return;
    setAvatarSelectionLoading(true);
    const sectionIndex = selectedSectionIndex;

    // For HeyGen avatars, don't update voice (no voice generation needed)
    const shouldUpdateVoiceForAvatar = avatarProvider === 'heygen' ? false : shouldUpdateVoice;

    if (updateAllScenes && shouldUpdateVoiceForAvatar) {
      // Generate voice for all scenes when updating all scenes
      const voiceResults: any[] = [];
      const getVoiceName = aiVoiceData?.voices?.find((voice: any) => voice.voice_id === voiceId)?.name;
      
      for (let i = 0; i < scriptDetails.data.length; i++) {
        const sectionData = scriptDetails.data[i];
        const text = sectionData.voiceOver;
        const enhancedText = sectionData?.enhancedVoiceOver;
        
        const bracketMatch = enhancedText?.match(/<[^>]+>/);
        const emotion = bracketMatch ? bracketMatch[0] : '';
        const newenhancedText = enhancedText ? emotion + ' ' + text : text;
        
        try {
          const voiceGenResult = await handleVoiceGeneration(newenhancedText, voiceId || '', getVoiceName || '');
          voiceResults[i] = {
            audioUrl: voiceGenResult.audioUrl,
            duration: voiceGenResult.duration,
            captionsResult: voiceGenResult.captionsResult,
            text: text,
            newenhancedText: newenhancedText
          };
        } catch (error) {
          console.error(`Error generating voice for section ${i}:`, error);
          // Use existing values if voice generation fails
          voiceResults[i] = {
            audioUrl: sectionData.url || '',
            duration: sectionData.sounds?.[0]?.audioDuration || 0,
            captionsResult: sectionData.captions?.[0] || null,
            text: text,
            newenhancedText: enhancedText || text
          };
        }
      }
      
      // Update all scenes in scriptDetails
      setScriptDetails((prevScript: { data: any }) => {
        const updatedScript = { ...prevScript };
        const updatedData = [...prevScript.data];
        
        for (let i = 0; i < updatedData.length; i++) {
          // Initialize aiAvatarProps if not exist
          if (!updatedData[i].aiAvatarProps) {
            updatedData[i].aiAvatarProps = {};
          }
          
          const voiceResult = voiceResults[i];
          // Update avatar properties for each scene
          updatedData[i] = {
            ...updatedData[i],
            sceneReplicaId: replicaId,
            previewUrl: previewUrl,
            voiceId: voiceId,
            captions: voiceResult.captionsResult ? [{ text: voiceResult.text, words: voiceResult.captionsResult.data?.words || voiceResult.captionsResult.words }] : undefined,
            sounds: [{ url: voiceResult.audioUrl, audioDuration: voiceResult.duration }],
            url: voiceResult.audioUrl,
            enhancedVoiceOver: voiceResult.newenhancedText,
          };
          
          // Only set voiceId for Tavus avatars (not for HeyGen)
          if (voiceId && avatarProvider === 'tavus') {
            updatedData[i].aiAvatarProps.voiceId = voiceId;
          }
        }
        
        return { ...updatedScript, data: updatedData };
      });
    } else if (updateAllScenes && !shouldUpdateVoiceForAvatar) {
      // Update all scenes with new avatar but keep existing voice
      setScriptDetails((prevScript: { data: any }) => {
        const updatedScript = { ...prevScript };
        const updatedData = [...prevScript.data];
        
        for (let i = 0; i < updatedData.length; i++) {
          // Initialize aiAvatarProps if not exist
          if (!updatedData[i].aiAvatarProps) {
            updatedData[i].aiAvatarProps = {};
          }
          
          // Update only avatar properties, keep existing voice data
          updatedData[i] = {
            ...updatedData[i],
            sceneReplicaId: replicaId,
            previewUrl: previewUrl,
            voiceId: voiceId,
          };
          
          // Only set voiceId for Tavus avatars (not for HeyGen)
          if (voiceId && avatarProvider === 'tavus') {
            updatedData[i].aiAvatarProps.voiceId = voiceId;
          }
        }
        
        return { ...updatedScript, data: updatedData };
      });
    } else {
      // Original logic for single scene only
      let audioUrl = '';
      let duration = 0;
      let captionsResult: any = null;
      let newenhancedText = '';
      let text = scriptDetails.data[sectionIndex].voiceOver;
      const enhancedText = scriptDetails.data[sectionIndex]?.enhancedVoiceOver;
      
      if (shouldUpdateVoiceForAvatar) {
        // Only call if user wants to update voice (not for HeyGen avatars)
        const bracketMatch = enhancedText?.match(/<[^>]+>/);
        const emotion = bracketMatch ? bracketMatch[0] : '';
        newenhancedText = enhancedText ? emotion + ' ' + text : text;
        const getVoiceName = aiVoiceData?.voices?.find((voice: any) => voice.voice_id === voiceId)?.name;
        const voiceGenResult = await handleVoiceGeneration(newenhancedText, voiceId || '', getVoiceName || '');
        audioUrl = voiceGenResult.audioUrl;
        duration = voiceGenResult.duration;
        captionsResult = voiceGenResult.captionsResult;
      } else {
        // Use existing values if not updating voice
        const section = scriptDetails.data[sectionIndex];
        audioUrl = section.url;
        duration = section.sounds?.[0]?.audioDuration || 0;
        captionsResult = section.captions?.[0] || null;
        newenhancedText = enhancedText;
        text = section.voiceOver;
      }
      
      setScriptDetails((prevScript: { data: any }) => {
        const updatedScript = { ...prevScript };
        const updatedData = [...prevScript.data];
        // Initialize aiAvatarProps if not exist
        if (!updatedData[sectionIndex].aiAvatarProps) {
          updatedData[sectionIndex].aiAvatarProps = {};
        }
        // Update avatar properties
        updatedData[sectionIndex] = {
          ...updatedData[sectionIndex],
          sceneReplicaId: replicaId,
          previewUrl: previewUrl,
          voiceId: voiceId,
          captions: captionsResult ? [{ text, words: captionsResult.data?.words || captionsResult.words }] : undefined,
          sounds: [{ url: audioUrl, audioDuration: duration }],
          url: audioUrl,
        };
        if (enhancedText && shouldUpdateVoiceForAvatar) {
          updatedData[sectionIndex].enhancedVoiceOver = newenhancedText;
        }
        // Only set voiceId for Tavus avatars (not for HeyGen)
        if (voiceId && avatarProvider === 'tavus') {
          updatedData[sectionIndex].aiAvatarProps.voiceId = voiceId;
        }
        return { ...updatedScript, data: updatedData };
      });
    }
    
    const avatarPage = store.getElementById(currentPageId);
    if(avatarPage?.children){
      if (updateAllScenes) {
        // Update all pages for all scenes
        store.pages.map((page: any) => {
          const pageScriptIndex = page.custom?.scriptIndex;
          if (pageScriptIndex !== undefined && scriptDetails.data[pageScriptIndex]) {
            const sectionData = scriptDetails.data[pageScriptIndex];
            const pageDuration = sectionData.sounds?.[0]?.audioDuration || 0;
            let newDuration = 3000;
            newDuration = (pageDuration*1000)/store.pages.filter((p: any) => p.custom.scriptIndex == pageScriptIndex).length;
            
            page.set({
              duration: newDuration,
              custom: {
                ...page.custom,
                sounds: sectionData.sounds || [],
                captions: sectionData.captions || [],
              },
            });
            
            // Update avatar in all pages
            const pageChildren = page.children;
            for (const child of pageChildren) {
              if (child.custom?.aiAvatar === true) {
                child.set({
                  src: previewUrl,
                  custom: {
                    ...child.custom,
                    avatarProvider: avatarProvider
                  }
                });
              }
            }
          }
        });
      } else {
        // Original logic for single scene only
        const currentSection = scriptDetails.data[sectionIndex];
        const audioUrl = currentSection.url || '';
        const duration = currentSection.sounds?.[0]?.audioDuration || 0;
        const text = currentSection.voiceOver || '';
        const captionsResult = currentSection.captions?.[0] || null;
        
        avatarPage.set({
          duration: duration*1000
        });
        const children = avatarPage.children;
        for (const page of children) {
          if (page.custom?.aiAvatar === true) {
            page.set({
              src: previewUrl,
              custom: {
                ...page.custom,
                avatarProvider: avatarProvider
              }
            });
          }
        }
        let newDuration = 3000;
        newDuration = (duration*1000)/store.pages.filter((page: any) => page.custom.scriptIndex == sectionIndex).length;
        store.pages.map((page: any) => {
          if(page.custom.scriptIndex == sectionIndex){
            page.set({
              duration: newDuration,
              custom: {
                ...page.custom,
                sounds: [{ url: audioUrl, audioDuration: duration }],
                captions: [{ text: text, words: captionsResult?.data?.words || captionsResult?.words }],
              },
            });
            // Update avatar in pages of current section only
            const pageChildren = page.children;
            for (const child of pageChildren) {
              if (child.custom?.aiAvatar === true) {
                child.set({
                  src: previewUrl,
                  custom: {
                    ...child.custom,
                    avatarProvider: avatarProvider
                  }
                });
              }
            }
          }
        });
      }
    }
    
    // Save customAvatar in the store
    setUserDetail((prevUserDetail: any) => ({
      ...prevUserDetail,
      purpose: {
        ...prevUserDetail?.purpose,
        avatarReplicaId: replicaId,
        selectedAVatarPreviewUrl: previewUrl,
        customAvatar: avatarProvider === 'heygen' ? true : false
      }
    }));
    
    setReplicaModalOpen(false);
    setJsonPages(store.toJSON());
    setAvatarSelectionLoading(false);
    setReplicaModalOpen(false);
  };
  
  const handleVoiceGeneration = async (text: string, voiceId: string, voiceName: string) => {
      const domainId = getDomainID();
      const token = getToken();
      const Prevnext = getPrevNextText(scriptDetails.data, selectedSectionIndex);
      const audioUrl = await aiTextToSpeech(
        text,
        voiceId,
        domainId,
        voiceName || 'Default',
        true,
        token,
        Prevnext.prevText,
        Prevnext.nextText,
        50,
        75
      );

      const duration = await getAudioDurationInSecondsCustom(audioUrl);
      let captionsResult:any = null;
      
      if (text.length > 0) {
        try {
          captionsResult = await generateCaptions(audioUrl, userDetail?.purpose?.language || '');
        } catch (e) {
          message.info('Could not generate captions for the audio');
        }
      }
      return {audioUrl, duration, captionsResult};
  }
  useEffect(() => {
    const prefetchAllAssets = async (jsonData: any) => {
      const assetPromises: Promise<void>[] = [];

      jsonData?.pages?.forEach((page: any) => {
        // Prefetch sounds if they exist
        // if (Array.isArray(page?.custom?.sounds) && page.custom.sounds.length > 0) {
        //   assetPromises.push(prefetchAsset(page.custom.sounds[0].url));
        // }

        // Prefetch child assets (images and videos)
        page.children.forEach((child: any) => {
          if ((child.type === "video" || child.type === "image") && child.src) {
            assetPromises.push(prefetchAsset(child.src));
          }
        });
      });

      // Wait for all assets to prefetch
      await Promise.all(assetPromises);
      await prefetchAsset(scriptDetails?.backgroundMusic?.url);
      await prefetchAsset(
        scriptDetails?.customTransitionSound ||
        "https://qa-dev-quickads-project.quickads.ai/image_projects/da764da2-2982-49be-88bd-eca414c4f5d7/assets/audio/e4cdb276-d1ad-4d81-91ae-3182d55c4099/audio.mp3"
      );
     scriptDetails?.data?.forEach((section: any) => {
      if(section.sounds){
        assetPromises.push(prefetchAsset(section.sounds[0].url));
      }
      if(section.transition?.url){
        assetPromises.push(prefetchAsset(section.transition.url));
      }
      transitionAnimationsDropDown.forEach((item: any) => {
        assetPromises.push(prefetchAsset(item.url));
      });
    
      
     })
     if (scriptDetails?.sfxs){
      scriptDetails?.sfxs.forEach((sfx: any) => {
        assetPromises.push(prefetchAsset(sfx.url));
      });
    
     }
    };
    prefetchAllAssets(jsonPages);
  }, [jsonPages, scriptDetails]);

  // Add a function to handle page deletion
  const handleDeletePage = (pageId: string, sectionIndex: number, imageIndex: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking delete icon
    const deepCopyScriptDetails = JSON.parse(JSON.stringify(scriptDetails))
    setUndoStack([...undoStack, deepCopyScriptDetails])
    // Get all pages from this section before deleting
    const sectionPages = store.pages.filter((page: any) => 
      page?.custom?.scriptIndex === sectionIndex
    );
    
    // Check if this is the last page in the section
    if (sectionPages.length <= 1) {
      message.warning('Cannot delete - each section must have at least one scene');
      return;
    }
    
    // Get the page being deleted to access its duration using getElementById
    const pageToDelete = store.getElementById(pageId);
    const deletedPageDuration = pageToDelete?.duration || 0;
    
    // Get pages to redistribute duration (all except the one being deleted)
    const pagesForRedistribution = sectionPages.filter((page: any) => page.id !== pageId);
    
    // Delete page from store
    store.deletePages([pageId]);
    
    // Redistribute the duration if we have other pages in this section
    if (pagesForRedistribution.length > 0 && deletedPageDuration > 0) {
      const durationPerPage = deletedPageDuration / pagesForRedistribution.length;
      
      // Add the distributed duration to each remaining page
      pagesForRedistribution.forEach((page: any) => {
        const currentDuration = page.duration || 0;
        store.getElementById(page.id).set({
          duration: currentDuration + durationPerPage
        });
      });
    }
    
    // Update scriptDetails to remove the corresponding image entry
    setScriptDetails((prevScript: { data: any }) => {
      const updatedScript = { ...prevScript };
      const updatedData = [...prevScript.data];
      
      // Only update if we have images array for this section
      if (updatedData[sectionIndex]?.images && Array.isArray(updatedData[sectionIndex].images)) {
        // Remove the image at imageIndex
        const updatedImages = [...updatedData[sectionIndex].images];
        updatedImages.splice(imageIndex, 1);
        
        updatedData[sectionIndex] = {
          ...updatedData[sectionIndex],
          images: updatedImages
        };
      }
      
      return { ...updatedScript, data: updatedData };
    });
    
    // Update jsonPages
    setJsonPages(store.toJSON());
    message.success('Media deleted successfully');
  };

  return (
    <div className="scene-media">
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <div className="sections-container" ref={sectionsContainerRef}>
          {scriptDetails?.data?.map((section: any, sectionIndex: number) => {
            const sectionPages = jsonPages?.pages?.filter((page: any) => page?.custom?.scriptIndex === sectionIndex) || [];
            const { startTime, endTime } = calculateSectionDuration(sectionIndex);

            return (
              <div key={sectionIndex} className="section-container">
                <div className="section-header">
                  <div className="section-linker">
                    <div className="linker-line">
                      <div className="linker-start">{`${(startTime / 1000).toFixed(1)}s`}</div>
                      <div className="linker-connector"></div>
                      <div className="linker-end">{`${(endTime / 1000).toFixed(1)}s`}</div>
                    </div>
                  </div>
                  <div className="section-title">
                    Section {sectionIndex + 1}
                  </div>
                  {/* <SectionPreviewButton
                    store={store}
                    previewOpen={previewOpen}
                    setPreviewOpen={setPreviewOpen}
                    scriptIndex={sectionIndex}
                    sectionId={sectionPages[0]?.id}
                  /> */}
                </div>

                {sectionPages.length > 0 ? (
                  <DragDropContext onDragEnd={(result) => handleDragEnd(result, sectionIndex)}>
                    <Droppable droppableId={`section-${sectionIndex}`} direction="horizontal">
                      {(provided) => (
                        <div
                          className="media-scroll-container"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {sectionPages.map((page: any, index: number) => {
                            const findPageMedia = page.children.filter((item: any) => item.type === 'image' || item.type === 'video');
                            const firstMedia = findPageMedia[0];
                            const startTime = parseFloat(getSceneStartTime(page.id));
                            const endTime = parseFloat(getSceneEndTime(page.id));
                            const havVideo = findPageMedia.find((page: any) => page.type === 'video');
                            return (
                              <Draggable
                                key={page.id}
                                draggableId={page.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`media-card ${snapshot.isDragging ? 'dragging' : ''}`}
                                    onClick={(e) => handleCardClick(page.id, e, sectionIndex, firstMedia.id, index)}
                                    data-page-id={page.id}
                                  >
                                    <div className="preview-container">
                                      <div className="media-preview">
                                        <div className="drag-handle-media">
                                          <HolderOutlined />
                                        </div>
                                        {!firstMedia && !firstMedia?.type ? (
                                          <div className='media-card-empty'>
                                            <div className='media-card-empty-text'>
                                              <Spin size="small" />
                                            </div>
                                          </div>
                                        ) : firstMedia.type === 'image' ? (
                                          <img src={firstMedia.src} alt={firstMedia.id} className="preview-image" />
                                        ) : (
                                          <video
                                            src={firstMedia.src}
                                            className="preview-image"
                                            controls={false}
                                            onPlay={() => handleVideoPlay(page.id, event as any)}
                                            onPause={() => handleVideoPause(page.id, event as any)}
                                            muted={true}
                                          />
                                        )}
                                        <div className="media-type-indicator">
                                          {!scriptDetails?.data[sectionIndex]?.aiAvatarProps?.avatar && (
                                            <div
                                              className="edit-icon"
                                              onClick={(e) => handleEditClick(page.id, firstMedia.id, sectionIndex, e, index)}
                                            >
                                              <EditOutlined />
                                            </div>
                                          )}
                                          {!scriptDetails?.data[sectionIndex]?.aiAvatarProps?.noAvatar && (
                                            <div
                                              className="avatar-icon"
                                              onClick={(e) => handleAvatarClick(page.id, firstMedia.id, sectionIndex, e)}
                                            >
                                              <UserOutlined />
                                            </div>
                                          )}
                                         
                                          {havVideo && (
                                            <div
                                              className="trim-icon"
                                              onClick={(e) => handleTrimClick(page.id, firstMedia.id, firstMedia.src, sectionIndex, e)}
                                            >
                                              <ScissorOutlined />
                                            </div>
                                          )}
                                          {!havVideo && (
                                            <div
                                              className="crop-icon"
                                              onClick={(e) => handleCropClick(page.id, firstMedia.id, firstMedia.src, firstMedia.type, sectionIndex, e)}
                                            >
                                              <GatewayOutlined />
                                            </div>
                                          )}

                                          {/* Add Delete Button with Confirmation */}
                                          <Popconfirm
                                            title="Delete this media?"
                                            description="This action cannot be undone."
                                            okText="Yes"
                                            cancelText="No"
                                            placement="topRight"
                                            onConfirm={(e) => handleDeletePage(page.id, sectionIndex, index, e as any)}
                                            onCancel={(e) => e?.stopPropagation()}
                                          >
                                            <div
                                              className="delete-icon"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <DeleteOutlined />
                                            </div>
                                          </Popconfirm>

                                          {firstMedia?.type === 'video' ? (
                                            playingVideo === page.id ? (
                                              <PauseCircleOutlined 
                                                className="video-icon" 
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleVideoPause(page.id, e);
                                                }}
                                              />
                                            ) : (
                                              <PlayCircleOutlined 
                                                className="video-icon" 
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleVideoPlay(page.id, e);
                                                }}
                                              />
                                            )
                                          ) : (
                                            <PictureOutlined className="image-icon" />
                                          )}
                                        </div>
                                      </div>
                                      <div className="scene-info">
                                        <div className="time-range">{`${startTime.toFixed(1)}s - ${endTime.toFixed(1)}s`}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {section?.aiAvatarProps?.noAvatar && (
                            <div className="add-media-card" onClick={() => handleAddMedia(sectionIndex)}>
                              <div className="add-media-content">
                                <PlusOutlined className="add-icon" />
                                <div className="add-text">Add Media</div>
                              </div>
                            </div>
                          )}

                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                ) : (
                  <div className="media-scroll-container">
                    <div className="add-media-card" onClick={() => handleAddMedia(sectionIndex)}>
                      <div className="add-media-content">
                        <PlusOutlined className="add-icon" />
                        <div className="add-text">Add Media</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Modal
        open={isModalOpen}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{
          height: 'calc(100vh - 110px)',
          padding: '24px',
          overflow: 'auto'
        }}
        title={editingPageId ? "Edit Media" : "Add Media"}
        footer={null}
      >
        <VideoPanel
          store={store}
          isNotPanel={false}
          searchQueries={scriptDetails?.data[selectedSectionIndex]?.images?.[selectedSubIndex]?.searchQueries || []}
          elementID={editingPageId || ""}
          index={selectedSectionIndex}
          subIndex={selectedSubIndex}
          searchQueriesFromParent={scriptDetails?.data[selectedSectionIndex]?.images?.[selectedSubIndex]?.searchQueries || []}
        />
      </Modal>

      {/* Trimmer Modal */}
      <Modal
        open={isTrimmerModalOpen}
        onCancel={handleTrimmerCancel}
        footer={null}
        width={800}
        title="Trim Video"
      >
        {trimmingElementId && trimmingVideoUrl && (
          <SceneMediaTrimmer
            videoUrl={trimmingVideoUrl}
            elementId={trimmingElementId}
            store={store}
            pageId={editingPageId || ""}
            onsave={handleTrimmerSave}
            onCancel={handleTrimmerCancel}
          />
        )}
      </Modal>

      {croppingMedia && (
        <CropMedia
          mediaUrl={croppingMedia.url}
          isVideo={croppingMedia.isVideo}
          store={store}
          elementId={croppingMedia.elementId}
          onSave={handleCropSave}
          onCancel={() => {
            setIsCropModalOpen(false);
            setCroppingMedia(null);
          }}
        />
      )}

      <SelectMediaModal
        isOpen={isSelectMediaModalOpen}
        onClose={() => setIsSelectMediaModalOpen(false)}
        mediaList={currentPageId ? store.getElementById(currentPageId)?.children.filter((item: any) => modalFor === 'crop' ? item.type === 'image' || item.type === 'video' : item.type === 'video') || [] : []}
        onSelectMedia={handleMediaSelect}
        modalFor={modalFor || 'crop'}
      />

      <ReplicaSelectionModal
        isOpen={replicaModalOpen}
        onClose={() => setReplicaModalOpen(false)}
        onSelectReplica={onAvatarChangeClick}
        selectedReplicaId={scriptDetails?.data[selectedSectionIndex]?.sceneReplicaId || userDetail?.purpose?.avatarReplicaId || ''}
        avatarSelectionLoading={avatarSelectionLoading}
      />

      {showPageScopeModal && (
        <Modal
          open={showPageScopeModal}
          onCancel={() => {
            setShowPageScopeModal(false);
            setPendingPageScopeChange(null);
          }}
          footer={null}
          width={400}
          title="Change Avatar Scope"
        >
          <p style={{ marginBottom: 24 }}>Do you want to change the avatar for all scenes or only the selected scene?</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button
              style={{
                background: 'var(--gradient-primary)',
                borderColor: 'transparent',
                color: 'white',
              }}
              onClick={() => {
                if (pendingPageScopeChange) {
                  // For HeyGen avatars, skip voice modal and directly update
                  if (pendingPageScopeChange.avatarProvider === 'heygen') {
                    handleReplicaSelect(
                      pendingPageScopeChange.replicaId,
                      pendingPageScopeChange.previewUrl,
                      undefined, // No voiceId for HeyGen
                      false, // Don't update voice
                      true, // Update all scenes
                      'heygen'
                    );
                  } else {
                    setPendingAvatarChange({
                      ...pendingPageScopeChange,
                      updateAllScenes: true
                    });
                    setShowAvatarChangeModal(true);
                  }
                }
                setShowPageScopeModal(false);
                setPendingPageScopeChange(null);
              }}
            >
              All scenes
            </Button>
            <Button
              style={{
                background: 'var(--gradient-primary)',
                borderColor: 'transparent',
                color: 'white',
              }}
              onClick={() => {
                if (pendingPageScopeChange) {
                  // For HeyGen avatars, skip voice modal and directly update
                  if (pendingPageScopeChange.avatarProvider === 'heygen') {
                    handleReplicaSelect(
                      pendingPageScopeChange.replicaId,
                      pendingPageScopeChange.previewUrl,
                      undefined, // No voiceId for HeyGen
                      false, // Don't update voice
                      false, // Update only selected scene
                      'heygen'
                    );
                  } else {
                    setPendingAvatarChange({
                      ...pendingPageScopeChange,
                      updateAllScenes: false
                    });
                    setShowAvatarChangeModal(true);
                  }
                }
                setShowPageScopeModal(false);
                setPendingPageScopeChange(null);
              }}
            >
              Selected scene only
            </Button>
          </div>
        </Modal>
      )}

      {showAvatarChangeModal && (
        <Modal
          open={showAvatarChangeModal}
          onCancel={() => setShowAvatarChangeModal(false)}
          footer={null}
          width={400}
          title="Change Avatar"
        >
          <p style={{ marginBottom: 24 }}>Do you want to also update the voice for this avatar?</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button
              style={{
                background: 'var(--gradient-primary)',
                borderColor: 'transparent',
                color: 'white',
              }}
              onClick={() => {
                if (pendingAvatarChange) {
                  handleReplicaSelect(
                    pendingAvatarChange.replicaId,
                    pendingAvatarChange.previewUrl,
                    pendingAvatarChange.voiceId,
                    true, // update voice
                    pendingAvatarChange.updateAllScenes ?? false, // use stored preference
                    pendingAvatarChange.avatarProvider || 'tavus'
                  );
                }
                setShowAvatarChangeModal(false);
                setPendingAvatarChange(null);
              }}
            >
              Yes, update voice
            </Button>
            <Button
              style={{
                background: 'var(--gradient-primary)',
                borderColor: 'transparent',
                color: 'white',
              }}
              onClick={() => {
                if (pendingAvatarChange) {
                  handleReplicaSelect(
                    pendingAvatarChange.replicaId,
                    pendingAvatarChange.previewUrl,
                    pendingAvatarChange.voiceId,
                    false, // do not update voice
                    pendingAvatarChange.updateAllScenes ?? false, // use stored preference
                    pendingAvatarChange.avatarProvider || 'tavus'
                  );
                }
                setShowAvatarChangeModal(false);
                setPendingAvatarChange(null);
              }}
            >
              No, just change avatar
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}); // Remove extra newline
export default SceneMedia;